import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { apiErrorMessage, useAuth } from "@/AuthContext";
import { RESET_PASSWORD } from "@/constants/testIds/auth";
import PublicFooter from "@/components/PublicFooter";

export default function ResetPasswordPage() {
  const { user, loading } = useAuth();
  const [params] = useSearchParams();
  const token = useMemo(() => (params.get("token") || "").trim(), [params]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!loading && user) {
    return <Navigate to={user.has_access ? "/dashboard" : "/billing?subscribe=1"} replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!token) {
      setError("This reset link is invalid or has expired.");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mp-auth">
      <div className="mp-auth-card">
        <p className="mp-kicker">MinutePay</p>
        <h1>Set a new password</h1>
        <p className="mp-lead">Choose a new password for your MinutePay account.</p>
        {done ? (
          <>
            <p className="mp-success">Your password was updated. You can sign in now.</p>
            <p className="mp-switch">
              <Link data-testid={RESET_PASSWORD.loginLink} to="/login">
                Sign in
              </Link>
            </p>
          </>
        ) : (
          <form onSubmit={onSubmit} className="mp-form">
            <label>
              New password
              <Input
                data-testid={RESET_PASSWORD.passwordInput}
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label>
              Confirm new password
              <Input
                data-testid={RESET_PASSWORD.passwordConfirmInput}
                type="password"
                required
                minLength={8}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            {error ? <p className="mp-error">{error}</p> : null}
            <Button data-testid={RESET_PASSWORD.submitButton} type="submit" disabled={busy} className="w-full">
              {busy ? "Saving…" : "Update password"}
            </Button>
          </form>
        )}
        {done ? null : (
          <p className="mp-switch">
            <Link data-testid={RESET_PASSWORD.loginLink} to="/login">
              Back to sign in
            </Link>
          </p>
        )}
      </div>
      <PublicFooter />
    </div>
  );
}
